import { Alert, Button, Card, Col, Empty, Input, List, Row, Space, Statistic, Tag, Typography } from 'antd';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type CopilotCounts = {
  eqams_draft_cycles: number | null;
  sip_drafts: number | null;
  app_plans_pending_approval: number | null;
  governance_open_actions: number | null;
};

type CopilotCycle = {
  id: string;
  title: string;
  status: string;
  item_readiness_counts: Record<string, number>;
  item_total: number;
};

type CopilotContext = {
  draft_only: true;
  advisory: true;
  counts: CopilotCounts;
  cycle: CopilotCycle | null;
  notes: string[];
};

type CopilotDraft = {
  kind: string;
  title: string;
  body: string;
  deep_link: string;
};

type CopilotDrafts = {
  draft_only: true;
  advisory: true;
  drafts: CopilotDraft[];
  notes: string[];
};

export default function QualityCopilotPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [cycleId, setCycleId] = useState('');
  const trimmedCycle = cycleId.trim();

  const contextQuery = useQuery({
    queryKey: ['education-quality-copilot', trimmedCycle],
    queryFn: async () =>
      (
        await api.get<CopilotContext>('/education/quality-copilot', {
          params: trimmedCycle ? { cycle_id: trimmedCycle } : undefined,
        })
      ).data,
  });

  const draftsMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<CopilotDrafts>('/education/quality-copilot/drafts', {
          cycle_id: trimmedCycle || undefined,
        })
      ).data,
  });

  const ctx = contextQuery.data;
  const drafts = draftsMutation.data;
  const notVisible = t('qualityCopilotNotVisible', { defaultValue: 'Not visible' });

  const stat = (value: number | null | undefined) => (value === null || value === undefined ? notVisible : value);

  return (
    <EducationPageChrome
      title={t('qualityCopilot', { defaultValue: 'Quality copilot' })}
      description={t('qualityCopilotIntro', {
        defaultValue:
          'Draft wording suggestions for School Improvement Plan and annual plan notes, composed from EQAMS, APP, and Governance facts you can already see.',
      })}
      tags={<Tag color="blue">{t('qualityCopilotDraftOnlyTag', { defaultValue: 'Draft only' })}</Tag>}
      extra={
        <Button onClick={() => navigate('/education')}>{t('backToHub', { defaultValue: 'Back' })}</Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={t('qualityCopilotScopeNote', {
            defaultValue:
              'Draft-only advisory text from fixed templates. Nothing is saved, no readiness level is changed, and nothing here is an accreditation, certification, or examination result. Edit and record wording yourself.',
          })}
        />

        <Card size="small" title={t('qualityCopilotCounts', { defaultValue: 'Facts in view' })} loading={contextQuery.isLoading}>
          {contextQuery.isError ? (
            <Alert type="error" showIcon message={t('error', { defaultValue: 'Error' })} />
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('qualityCopilotEqamsDrafts', { defaultValue: 'EQAMS draft cycles' })}
                  value={stat(ctx?.counts.eqams_draft_cycles)}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('qualityCopilotSipDrafts', { defaultValue: 'SIP drafts' })}
                  value={stat(ctx?.counts.sip_drafts)}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('qualityCopilotAppPending', { defaultValue: 'APP plans pending approval' })}
                  value={stat(ctx?.counts.app_plans_pending_approval)}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title={t('qualityCopilotGovernanceOpen', { defaultValue: 'Open governance actions' })}
                  value={stat(ctx?.counts.governance_open_actions)}
                />
              </Col>
            </Row>
          )}
        </Card>

        <Card size="small" title={t('qualityCopilotCycleOptional', { defaultValue: 'EQAMS cycle (optional)' })}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              allowClear
              value={cycleId}
              maxLength={36}
              onChange={(e) => setCycleId(e.target.value)}
              placeholder="cycle id"
            />
            <Typography.Text type="secondary">
              {t('qualityCopilotCycleHint', {
                defaultValue: 'Paste a cycle id to include readiness-level counts for that cycle.',
              })}
            </Typography.Text>
            {ctx?.cycle ? (
              <Space direction="vertical" size={4}>
                <Typography.Text strong>
                  {ctx.cycle.title} <Tag>{ctx.cycle.status}</Tag>
                </Typography.Text>
                <Typography.Text type="secondary">
                  {t('qualityCopilotCycleSummary', { defaultValue: 'Cycle readiness counts' })}
                </Typography.Text>
                <Space wrap>
                  {Object.entries(ctx.cycle.item_readiness_counts).map(([level, count]) => (
                    <Tag key={level}>
                      {level.replace(/_/g, ' ')}: {count}
                    </Tag>
                  ))}
                </Space>
              </Space>
            ) : null}
          </Space>
        </Card>

        <Card
          size="small"
          title={t('qualityCopilotDrafts', { defaultValue: 'Draft suggestions' })}
          extra={
            <Button type="primary" loading={draftsMutation.isPending} onClick={() => draftsMutation.mutate()}>
              {t('qualityCopilotGenerate', { defaultValue: 'Generate drafts' })}
            </Button>
          }
        >
          {draftsMutation.isError ? (
            <Alert type="error" showIcon message={t('error', { defaultValue: 'Error' })} style={{ marginBottom: 12 }} />
          ) : null}
          {drafts && drafts.drafts.length > 0 ? (
            <List
              itemLayout="vertical"
              dataSource={drafts.drafts}
              renderItem={(draft) => (
                <List.Item
                  key={`${draft.kind}-${draft.title}`}
                  actions={[
                    <Button key="open" size="small" type="link" onClick={() => navigate(draft.deep_link)}>
                      {t('qualityCopilotOpenTarget', { defaultValue: 'Open and edit' })}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {draft.title}
                        <Tag>{t('qualityCopilotDraftOnlyTag', { defaultValue: 'Draft only' })}</Tag>
                      </Space>
                    }
                  />
                  <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{draft.body}</Typography.Paragraph>
                </List.Item>
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('qualityCopilotNoDrafts', {
                defaultValue: 'No drafts yet. Generate drafts to see suggested wording.',
              })}
            />
          )}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
